import type { SelectedCustomization } from '@cracked-mug/shared';
import { computeLineTotal, formatPrice } from '@cracked-mug/shared';

export interface OrderSummaryLine {
  key: string;
  name: string;
  quantity: number;
  basePriceCents: number;
  selectedCustomizations: SelectedCustomization[];
  specialInstructions: string;
}

interface OrderSummaryProps {
  items: OrderSummaryLine[];
  subtotalCents: number;
  taxCents: number;
  tipCents: number;
  totalCents: number;
}

function Row({ label, cents, bold }: { label: string; cents: number; bold?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={bold ? 'font-semibold' : 'text-wca-gray-mid'}>{label}</span>
      <span className={bold ? 'font-mono text-lg tabular-nums' : 'font-mono text-sm tabular-nums'}>
        {formatPrice(cents)}
      </span>
    </div>
  );
}

export function OrderSummary({
  items,
  subtotalCents,
  taxCents,
  tipCents,
  totalCents,
}: OrderSummaryProps) {
  return (
    <div className="bg-wca-surface rounded-card shadow-card p-5 space-y-4">
      <h2 className="font-display text-xl">Order summary</h2>
      <ul className="divide-y divide-wca-gray-light">
        {items.map((line) => {
          const lineTotal = computeLineTotal(
            line.basePriceCents,
            line.selectedCustomizations,
            line.quantity,
          );
          const customizationSummary = line.selectedCustomizations
            .filter((c) => c.optionLabels.length > 0)
            .map((c) => c.optionLabels.join(', '))
            .join(' · ');
          return (
            <li key={line.key} className="py-3 first:pt-0 last:pb-0 flex justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-sm">
                  {line.quantity}× {line.name}
                </p>
                {customizationSummary.length > 0 && (
                  <p className="text-xs text-wca-gray-mid mt-0.5">{customizationSummary}</p>
                )}
                {line.specialInstructions.length > 0 && (
                  <p className="text-xs italic text-wca-gray-mid mt-0.5">
                    &ldquo;{line.specialInstructions}&rdquo;
                  </p>
                )}
              </div>
              <p className="font-mono text-sm text-wca-red whitespace-nowrap">
                {formatPrice(lineTotal)}
              </p>
            </li>
          );
        })}
      </ul>
      <div className="space-y-1 pt-2 border-t border-wca-gray-light">
        <Row label="Subtotal" cents={subtotalCents} />
        <Row label="Tax" cents={taxCents} />
        {tipCents > 0 && <Row label="Tip" cents={tipCents} />}
        <Row label="Total" cents={totalCents} bold />
      </div>
    </div>
  );
}
